import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Search, Package, Calendar, User, AlertCircle, CheckCircle, Clock, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const DemandesEPI = () => {
  const navigate = useNavigate();
  const [demandes, setDemandes] = useState([]);
  const [filteredDemandes, setFilteredDemandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statutFilter, setStatutFilter] = useState('tous');
  const [userRole, setUserRole] = useState('');

  const loadUserRole = useCallback(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setUserRole(user.role || '');
  }, []);

  const loadDemandes = useCallback(async () => {
    try {
      const response = await api.get('/demandes-epi');
      setDemandes(response.demandes || []);
    } catch (error) {
      console.error('Error loading demandes:', error);
      if (error.status === 403) {
        toast.error('Accès refusé');
      } else {
        toast.error('Erreur lors du chargement des demandes');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDemandes();
    loadUserRole();
  }, [loadDemandes, loadUserRole]);

  useEffect(() => {
    filterDemandes();
  }, [searchTerm, statutFilter, demandes]);

  const filterDemandes = () => {
    let filtered = [...demandes];

    if (searchTerm) {
      filtered = filtered.filter(d =>
        d.collaborateur_nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.collaborateur_matricule?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.type_epi_nom?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statutFilter && statutFilter !== 'tous') {
      filtered = filtered.filter(d => d.statut === statutFilter);
    }

    setFilteredDemandes(filtered);
  };

  const getStatutBadge = (statut) => {
    const config = {
      'En attente': { icon: Clock, color: 'bg-orange-100 text-orange-800', label: 'En attente' },
      'En cours': { icon: Clock, color: 'bg-blue-100 text-blue-800', label: 'En cours' },
      'Livrée': { icon: CheckCircle, color: 'bg-green-100 text-green-800', label: 'Livrée' },
      'Rejetée': { icon: XCircle, color: 'bg-red-100 text-red-800', label: 'Rejetée' }
    };

    const { icon: Icon, color, label } = config[statut] || config['En attente'];

    return (
      <Badge className={`${color} gap-1`}>
        <Icon className="h-3 w-3" />
        {label}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Package className="h-8 w-8 text-blue-600" />
            Demandes HSE
          </h1>
          <p className="text-gray-600 mt-2">
            Gestion des demandes d'équipements de protection individuelle
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={() => navigate('/epi/dashboard')}
            variant="outline"
            data-testid="btn-dashboard"
          >
            Tableau de bord
          </Button>
          {(userRole === 'Manager' || userRole === 'HSE' || userRole === 'Admin') && (
            <Button
              onClick={() => navigate('/epi/nouvelle-demande')}
              className="gap-2"
              data-testid="btn-nouvelle-demande"
            >
              <Plus className="h-4 w-4" />
              Nouvelle demande
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Rechercher par nom, matricule ou type..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>
            <Select value={statutFilter} onValueChange={setStatutFilter}>
              <SelectTrigger data-testid="filtre-statut">
                <SelectValue placeholder="Filtrer par statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tous">Tous les statuts</SelectItem>
                <SelectItem value="En attente">En attente</SelectItem>
                <SelectItem value="En cours">En cours</SelectItem>
                <SelectItem value="Livrée">Livrée</SelectItem>
                <SelectItem value="Rejetée">Rejetée</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-600">Total</p>
              <p className="text-3xl font-bold">{demandes.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-orange-600">En attente</p>
              <p className="text-3xl font-bold text-orange-600">
                {demandes.filter(d => d.statut === 'En attente').length}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-blue-600">En cours</p>
              <p className="text-3xl font-bold text-blue-600">
                {demandes.filter(d => d.statut === 'En cours').length}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-green-600">Livrées</p>
              <p className="text-3xl font-bold text-green-600">
                {demandes.filter(d => d.statut === 'Livrée').length}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Liste des demandes */}
      <Card>
        <CardHeader>
          <CardTitle>Liste des demandes ({filteredDemandes.length})</CardTitle>
          <CardDescription>
            Cliquez sur une demande pour voir les détails
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredDemandes.length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">Aucune demande trouvée</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredDemandes.map((demande) => (
                <Card
                  key={demande.id}
                  className="hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/epi/demandes/${demande.id}`)}
                  data-testid={`demande-${demande.id}`}
                >
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="flex items-center gap-3">
                          <User className="h-5 w-5 text-gray-400" />
                          <div>
                            <p className="font-medium">{demande.collaborateur_nom}</p>
                            <p className="text-sm text-gray-500">{demande.collaborateur_matricule}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Package className="h-5 w-5 text-gray-400" />
                          <div>
                            <p className="font-medium">{demande.type_epi_nom}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Calendar className="h-5 w-5 text-gray-400" />
                          <div>
                            <p className="text-sm text-gray-600">Demandé le</p>
                            <p className="text-sm font-medium">{formatDate(demande.date_demande)}</p>
                          </div>
                        </div>
                        <div className="flex items-center justify-end">
                          {getStatutBadge(demande.statut)}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DemandesEPI;